/**
 * Permissions service - fetches current user's allowed roles
 * TODO: Replace mock with real API call when backend is ready
 */

import type { UserRole } from "../constants/permissions";
import { apiClient } from "./apiClient";

/**
 * Interface for privileges response (GET /authz/privileges)
 */
export interface UserPrivilegesData {
  privileges: string[];
}

/**
 * All privilege codes used in the app (for reference).
 */
export const ALL_PRIVILEGES: string[] = [
  "gri.regulation.read",
  "gri.regulation.saved_filter",
  "gri.regulation.export",
  "gri.regulation.view_mpr",
  "gri.regulation.view_cos",
  "gri.regulation.view_mdv",
  "gri.regulation.view_chp",
  "gri.regulation.view_fds",
  "gri.assessment.checklist",
  "gri.assessment.config.process_mapping",
  "gri.assessment.impact.read",
  "gri.assessment.impact.execute",
  "gri.assessment.impact.system.execute",
  // "gri.assessment.manual.execute",
  // "gri.assessment.manual.read",
  "gri.assessment.comparison.read",
  "gri.assessment.comparison.execute",
  "gri.assessment.manual.process.manage",
  "gri.assessment.manual.process.terminate",
  "gri.assessment.manual.pdf.view",
  "gri.assessment.manual.pdf.download",
  "gri.assessment.manual.pdf.share",
  "gri.auto_tagging.read",
  "gri.auto_tagging.manage",
  "gri.auto_tagging.export",
  "gri.auto_tagging.execute",
  "gri.regulation.assessment_pack.read",
  "gri.regulation.auto_tagging.read",
];

/** Domain dropdown view privileges (`gri.regulation.view_*`). */
export const DOMAIN_VIEW_PRIVILEGE_CODES = [
  "gri.regulation.view_mpr",
  "gri.regulation.view_cos",
  "gri.regulation.view_mdv",
  "gri.regulation.view_chp",
  "gri.regulation.view_fds",
] as const;
/** Manual IA PDF Actions privileges (`GET /authz/privileges`). */
export const MANUAL_IA_PDF_PRIVILEGES = {
  view: "gri.assessment.manual.pdf.view",
  download: "gri.assessment.manual.pdf.download",
  share: "gri.assessment.manual.pdf.share",
} as const;

/** Auto-tagging / Organisational AI Plan privilege codes. */
export const AUTO_TAGGING_PRIVILEGES = {
  read: "gri.auto_tagging.read",
  manage: "gri.auto_tagging.manage",
  export: "gri.auto_tagging.export",
  execute: "gri.auto_tagging.execute",
} as const;

/** Insight detail Impact Assessment package card. */
export const REGULATION_ASSESSMENT_PACK_PRIVILEGES = {
  read: "gri.regulation.assessment_pack.read",
} as const;

/** Insight detail Classification Result and Conditional Classification Result. */
export const REGULATION_AUTO_TAGGING_PRIVILEGES = {
  read: "gri.regulation.auto_tagging.read",
} as const;

/**
 * Mock: Returns the permissions (allowed roles) for the current user.
 * Each permission maps to a role the user can assume.
 *
 * Examples:
 * - ["admin"] -> user only sees/uses Admin role
 * - ["admin", "triage_officer"] -> user can switch between Admin and Triage Officer
 * - ["department_assessor", "department_approver"] -> user can switch between those two
 */
export async function getCurrentUserPermissions(): Promise<UserRole[]> {
  // Mock: simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 0));

  // Mock: current user can assume all four roles (for dev/demo).
  // Restrict to test specific role: e.g. ["admin"] or ["triage_officer", "department_assessor"]
  return [
    "admin",
    "triage_officer",
    "department_assessor",
    "department_approver",
  ] as UserRole[];
}

/**
 * Fetches current user's privileges from the backend.
 */
export async function getCurrentUserPrivileges(): Promise<string[]> {
  try {
    const result = await apiClient.get<UserPrivilegesData>("/authz/privileges");
    if (result.status === "success" && result.data?.privileges?.length) {
      return result.data.privileges;
    }
    return [];
  } catch (error) {
    console.error("Failed to fetch user privileges:", error);
    return [];
  }
}
